import { useEffect, useState } from "react";
import { Loader2, Printer, FileDown, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Modal } from "@/components/Modal";
import { PrinterSetup } from "@/components/docs/PrinterSetup";
import { getPrinter, qzPrintRaw, qzErrorText } from "@/lib/qz";

async function blobError(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try { return JSON.parse(await d.text()).detail || "Gagal membuat picking list"; } catch { return "Gagal membuat picking list"; }
  }
  return e?.response ? apiError(e) : qzErrorText(e);
}

function Skipped({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="pick-skipped" data-testid="picking-skipped">
      <b>{rows.length} order dilewati</b>
      {rows.slice(0, 6).map((s) => <small key={s.id}>{s.number || s.id.slice(0, 8)} — {s.reason}</small>)}
      {rows.length > 6 && <small>…dan {rows.length - 6} lainnya</small>}
    </div>
  );
}

function ProductTable({ products }) {
  return (
    <table className="pick-table" data-testid="picking-products">
      <thead><tr><th>Kode</th><th>Produk</th><th className="r">Total Qty</th><th>Satuan</th><th className="r">Jml DO</th></tr></thead>
      <tbody>
        {products.map((p) => (
          <tr key={p.product_odoo_id} data-testid={`picking-product-${p.product_odoo_id}`}>
            <td>{p.code || "-"}</td><td>{p.product}</td><td className="r"><b>{p.qty}</b></td><td>{p.uom}</td><td className="r">{p.n_orders}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Warehouse picking list: per-product totals + per-DO detail. body = {today:true} or {orders:[ids]}.
export function PickingListDialog({ body, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [setup, setSetup] = useState(false);

  useEffect(() => {
    api.post("/documents/picking-list", body).then((r) => setData(r.data.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pdf = async () => {
    setBusy("pdf");
    try {
      const r = await api.post("/documents/picking-list/pdf", body, { responseType: "blob" });
      const name = ((r.headers["content-disposition"] || "").match(/filename="([^"]+)"/) || [])[1] || "Picking-List.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30000);
      toast.success(`Picking list ${r.headers["x-doc-count"] || ""} DO diunduh`);
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(null); }
  };

  const print = async () => {
    const printer = getPrinter();
    if (!printer) { toast.info("Pilih printer dot-matrix dulu"); setSetup(true); return; }
    setBusy("print");
    try {
      const r = await api.post("/documents/picking-list/raw", body);
      const d = r.data.data;
      await qzPrintRaw(printer, d.base64, `Picking list ${d.number}`);
      toast.success(`Picking list ${d.number} (${d.pages} halaman) dikirim ke ${printer}`);
    } catch (e) { toast.error(await blobError(e)); } finally { setBusy(null); }
  };

  const empty = data && data.orders.length === 0;
  return (
    <Modal open onClose={onClose} className="picking-dialog" testId="picking-dialog"
      title={body.today ? "Picking List DO Hari Ini" : "Picking List DO Pilihan"}
      subtitle={data?.note || "Menyiapkan daftar barang…"}
      footer={
        <>
          <button className="rj-btn rj-btn-ghost" onClick={onClose} data-testid="picking-close-btn">Tutup</button>
          <button className="rj-btn rj-btn-green" onClick={pdf} disabled={!data || empty || !!busy} data-testid="picking-pdf-btn">
            {busy === "pdf" ? <Loader2 size={15} className="spin" /> : <FileDown size={15} />} Unduh PDF</button>
          <button className="rj-btn rj-btn-primary" onClick={print} disabled={!data || empty || !!busy} data-testid="picking-print-btn">
            {busy === "print" ? <Loader2 size={15} className="spin" /> : <Printer size={15} />} Print dot-matrix</button>
        </>
      }>
      {!data ? <div className="rj-loading"><Loader2 className="spin" /> Memuat…</div> : empty ? (
        <div className="rj-empty" data-testid="picking-empty"><ClipboardList />
          <p>{body.today ? "Belum ada DO yang perlu disiapkan hari ini." : "Tidak ada DO yang perlu disiapkan dari order yang dipilih."}</p>
          <Skipped rows={data.skipped} />
        </div>
      ) : (
        <div className="picking-body">
          <div className="pick-stats">
            <div><span>DO</span><strong data-testid="picking-order-count">{data.orders.length}</strong></div>
            <div><span>Jenis produk</span><strong data-testid="picking-product-count">{data.products.length}</strong></div>
            <div><span>Total qty</span><strong data-testid="picking-total-qty">{data.total_qty}</strong></div>
          </div>
          <h4>Rekap per produk</h4>
          <ProductTable products={data.products} />
          <h4>DO yang disiapkan</h4>
          <div className="pick-orders" data-testid="picking-orders">
            {data.orders.map((o) => (
              <div key={o.id} data-testid={`picking-order-${o.so_number}`}>
                <b>{o.so_number}</b><span>{o.outlet}</span>
                <small>{o.lines} produk · {o.qty} unit{o.delivery_date ? ` · kirim ${fmtDate(o.delivery_date)}` : ""}</small>
              </div>
            ))}
          </div>
          <Skipped rows={data.skipped} />
        </div>
      )}
      {setup && <PrinterSetup onClose={() => setSetup(false)} />}
    </Modal>
  );
}
